import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@mui/material';
import _ from 'lodash';
import DOMPurify from 'dompurify';

import { PsButtonTuner, PsCheckboxTuner } from 'stencilSetup';
import { selectSubscriberDetails } from 'enrollment/features/Profile/services';
import { displayDependentAvatar } from 'enrollment/features/Family/FamilyDetailsPage/components';
import { selectDependentDetails } from 'enrollment/features/Family/services';
import { getDependentAge } from 'enrollment/features/Shared/FamilySelect/utils';
import { OrbitDrawer } from 'enrollment/components/Shared/OrbitDrawer';
import {
  ProviderFamilyInfoCardStyled,
  ProviderInfoBoxStyled,
  ProviderInfoCardStyled,
} from './ProvideInfoDrawerStyled';
import { ProviderFamilyBoxStyled } from '../SearchResultsPCPDrawer/SearchResultsPCPDrawerStyled';
import {
  selectPCPProviderInfoLogic,
  setCheckedDependentCheckbox,
  setCheckedDependentCheckboxId,
  setCheckSubscriberExistingPatientCheckbox,
  setDependentProviderItems,
  setHideSubscriberCheckboxWhenChecked,
  setOpenChoosePCPDrawer,
  setOpenSelectProviderDrawer,
  setPcpDentalData,
  setSubscriberCheckbox,
} from '../SearchResultsPCPDrawer/services';
import { selectProviderApiData } from '../services';
import { PcpRequestDataDto, AdditionalPlanElectionDataDto } from '../../../types';
import {
  BenefitPlanDto,
  PcpMembersDto,
} from 'enrollment/queries/useBenefitPlansQuery/types';
import { PageContentDetails } from 'enrollment/components';
import { PageContentQueryResponseDto } from 'enrollment/queries/usePageContentQuery/types';

interface ProvideInfoDrawerProps {
  setPcpElectionData: (data: AdditionalPlanElectionDataDto) => void;
  setPcpDrawerRequired: (value: boolean) => void;
  pcpMembers: PcpMembersDto | PcpRequestDataDto | undefined;
  selectedDependentIds: number[] | undefined;
  openChooseProviderDrawer: boolean;
  setOpenChooseProviderDrawer: (value: boolean) => void;
  setShowPcpDrawer: (value: boolean) => void;
  plan?: BenefitPlanDto;
  setHideDependentCheckboxWhenChecked: (value: any) => void;
  pageContent: PageContentQueryResponseDto | undefined;
}

export const ProvideInfoDrawer = ({
  setPcpElectionData,
  setPcpDrawerRequired,
  pcpMembers,
  selectedDependentIds,
  openChooseProviderDrawer,
  setOpenChooseProviderDrawer,
  setShowPcpDrawer,
  plan,
  setHideDependentCheckboxWhenChecked,
  pageContent,
}: ProvideInfoDrawerProps) => {
  const [
    disableProviderDrawerInfoSaveButton,
    setDisableDrawerProviderInfoSaveButton,
  ] = useState<boolean>(true);

  const subscriberData = useSelector(selectSubscriberDetails);
  const dependentSelectorData = useSelector(selectDependentDetails);

  const dependentData = dependentSelectorData?.dependents?.filter(
    (dependent: any) => selectedDependentIds?.includes(dependent?.id)
  );

  const { t } = useTranslation('enrollment.pcpProvideInfoDrawer');

  const providerItem = useSelector(selectProviderApiData);

  const providerInfoLogicSelector = useSelector(selectPCPProviderInfoLogic);

  const checkSubscriberCheckbox =
    providerInfoLogicSelector?.isSubscriberCheckboxChecked;

  const checkDependentCheckbox =
    providerInfoLogicSelector?.isDependentCheckboxChecked;

  const subscriberProviderData =
    providerInfoLogicSelector?.subscriberProviderItems;

  const saveCheckedDependentDataSelector =
    providerInfoLogicSelector?.dependentProviderItems;

  const isSelectProviderDrawerOpen =
    providerInfoLogicSelector?.isSelectProviderDrawerOpen;

  const subscriberProviderDataExistingPatient =
    providerInfoLogicSelector?.isSubscriberExistingPatientCheck;

  const dispatch = useDispatch();

  const handleSubscriberSearchProvider = () => {
    dispatch(setOpenSelectProviderDrawer(false));
    setOpenChooseProviderDrawer(false);
    dispatch(setOpenChoosePCPDrawer(true));
    dispatch(setHideSubscriberCheckboxWhenChecked(true));
  };

  const handleDependentSearchProvider = () => {
    dispatch(setOpenSelectProviderDrawer(false));
    setOpenChooseProviderDrawer(false);
    dispatch(setOpenChoosePCPDrawer(true));

    dependentData.forEach((dependent: any) => {
      const dependentId = `dependent-provider-${dependent?.id}`;
      setHideDependentCheckboxWhenChecked((prevState: any) => {
        const newState = { ...prevState };

        if (Object.keys(checkDependentCheckbox)?.length === 0) {
          dependentData.forEach((dep: any) => {
            const id = `dependent-provider-${dep?.id}`;
            newState[id] = false;
          });
        } else {
          if (prevState[dependentId] !== true) {
            newState[dependentId] = false;
          }
        }

        return newState;
      });
    });
  };

  const handleChangeSubscriberProvider = () => {
    dispatch(setHideSubscriberCheckboxWhenChecked(true));

    dispatch(setSubscriberCheckbox(false));

    dispatch(setOpenSelectProviderDrawer(false));
    dispatch(setCheckSubscriberExistingPatientCheckbox(false));
    setOpenChooseProviderDrawer(false);
    dispatch(setOpenChoosePCPDrawer(true));
  };

  const handleChangeDependentProvider = (e: any, dependentId: any) => {
    dispatch(setHideSubscriberCheckboxWhenChecked(false));
    setHideDependentCheckboxWhenChecked({ [dependentId]: false });

    dispatch(setCheckedDependentCheckboxId(dependentId));
    dispatch(setCheckedDependentCheckbox({ id: dependentId, checked: false }));
    dispatch(setOpenSelectProviderDrawer(false));
    setOpenChooseProviderDrawer(false);
    dispatch(setOpenChoosePCPDrawer(true));
  };

  const handleCloseChooseProviderDrawer = () => {
    dispatch(setHideSubscriberCheckboxWhenChecked(true));

    setHideDependentCheckboxWhenChecked((prevState: any) => {
      const newState = { ...prevState };
      dependentData.forEach((dependent: any) => {
        const dependentId = `dependent-provider-${dependent?.id}`;
        newState[dependentId] = false;
      });
      return newState;
    });

    setOpenChooseProviderDrawer(false);
    setShowPcpDrawer(false);
    dispatch(setOpenSelectProviderDrawer(false));
    dispatch(setOpenChoosePCPDrawer(false));
    const hasDrawerOpenClass = document.querySelector('.drawer-open');
    if (hasDrawerOpenClass) {
      hasDrawerOpenClass.classList.remove('drawer-open');
    }
  };

  const handleChangeSubscriberExistingPatient = (e: any) => {
    if (e?.target?.tagName === 'INPUT') {
      dispatch(setCheckSubscriberExistingPatientCheckbox(e?.target?.checked));
    }
  };

  const updateDependentData = (
    prevState: any,
    e: any,
    dependentData: any[],
    providerItem: any
  ) => {
    const newState = [...prevState]; // Clone the previous state to avoid direct mutation
    const matchingItem = pcpMembers?.dependents?.filter((dependent: any) =>
      selectedDependentIds?.includes(dependent?.id)
    );
    const dependentLoopingCondition =
      matchingItem && matchingItem?.length > 0 ? matchingItem : dependentData;

    const targetIDRegex = e?.target?.id;
    const matchTargetId = targetIDRegex?.match(/(\d+)$/)[1];

    dependentLoopingCondition?.forEach((dependent) => {
      const index = newState.findIndex(
        (item: any) => item.dependent.id === dependent.id
      );

      if (index !== -1) {
        // If the item exists, update only the existingPatient value
        const previousChecked = newState[index].existingPatient;
        newState[index] = {
          ...newState[index],
          existingPatient:
            Number(matchTargetId) === dependent.id
              ? e?.target?.checked
              : previousChecked,
        };
      } else {
        // Add as a new item if it doesn't exist
        const newItem = {
          ...providerItem,
          id: dependent?.id,
          dependent,
          existingPatient:
            Number(matchTargetId) === dependent.id
              ? e?.target?.checked
              : dependent?.existing_patient,
          providerName: dependent?.pcp_name,
          providerNetworkCode: dependent?.pcp_code,
        };
        newState.push(newItem);
      }
    });

    return newState;
  };

  const handleChangeDependentExistingPatient = (
    e: any,
    dependentData: any,
    providerItem: any
  ) => {
    if (e?.target?.tagName === 'INPUT') {
      const newState = updateDependentData(
        saveCheckedDependentDataSelector,
        e,
        dependentData,
        providerItem
      );
      dispatch(setDependentProviderItems(newState));
    }
  };

  const dependentSelectedIdPcpData = pcpMembers?.dependents?.filter(
    (dependent: any) => selectedDependentIds?.includes(dependent?.id)
  );

  const requiredSaveCheckedDependentDataSelector =
    Array.isArray(saveCheckedDependentDataSelector) &&
    saveCheckedDependentDataSelector?.filter((dependent: any) =>
      selectedDependentIds?.includes(dependent?.id)
    );

  const pcpMembersSubmitDependentData =
    Object.keys(saveCheckedDependentDataSelector)?.length !== 0
      ? requiredSaveCheckedDependentDataSelector
      : dependentSelectedIdPcpData;

  const subscriberProviderDataName = subscriberProviderData?.name
    ? subscriberProviderData?.name
    : pcpMembers?.pcp_name;

  const subscriberProviderDataNetworkCode = subscriberProviderData?.network_code
    ? subscriberProviderData?.network_code
    : pcpMembers?.pcp_code;

  const mappedPcpDependentData =
    Array.isArray(pcpMembersSubmitDependentData) &&
    pcpMembersSubmitDependentData?.map((data: any) => ({
      id: data?.dependent?.id ? data?.dependent?.id : data?.id,
      pcpCode: data?.providerNetworkCode
        ? data?.providerNetworkCode
        : data?.network_code
          ? data?.network_code
          : data?.pcp_code,
      pcpName: data?.providerName
        ? data?.providerName
        : data?.name
          ? data?.name
          : data?.pcp_name,
      existingPatient: data?.existingPatient ? data?.existingPatient : false,
    }));

  const pcpDentalData = {
    pcpCode: subscriberProviderDataNetworkCode,
    pcpName: subscriberProviderDataName,
    existingPatient: subscriberProviderDataExistingPatient !== undefined ? subscriberProviderDataExistingPatient : false,
    dependents: mappedPcpDependentData,
  };

  const handleSubmitProviderInfoDetails = () => {
    dispatch(setPcpDentalData(pcpDentalData));

    // handling the election and opening e-signature modal has been moved to PlanCard.tsx
    // to handle multiple popups we are just setting the additional election data here
    setPcpElectionData({
      pcpFormData: pcpDentalData,
    });
    setPcpDrawerRequired(false);

    setShowPcpDrawer(false);
    dispatch(setOpenSelectProviderDrawer(false));
    dispatch(setSubscriberCheckbox(false));
    dispatch(setOpenChoosePCPDrawer(false));
  };

  const requiredAllDependentChecked = Object.entries(checkDependentCheckbox)
    .filter(([key]: [string, boolean]) => {
      const matchTargetKey = key?.match(/(\d+)$/)?.[1];
      return selectedDependentIds?.includes(Number(matchTargetKey));
    })
    .map(([, value]: [string, boolean]) => value);

  const allCheckDependentCheckboxTrue = requiredAllDependentChecked?.every(
    (value) => value === true
  );

  const hasProviderInfoDataWithoutPcp = Boolean(
    requiredAllDependentChecked?.length === dependentData?.length &&
    subscriberProviderData?.network_code &&
    subscriberProviderData?.name
  );

  useEffect(() => {
    if (
      (hasProviderInfoDataWithoutPcp && allCheckDependentCheckboxTrue) ||
      pcpMembers?.pcp_name ||
      (pcpMembers?.dependents && pcpMembers?.dependents?.length > 0)
    ) {
      setDisableDrawerProviderInfoSaveButton(false);
    } else {
      setDisableDrawerProviderInfoSaveButton(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasProviderInfoDataWithoutPcp,
    allCheckDependentCheckboxTrue,
    subscriberProviderData,
    disableProviderDrawerInfoSaveButton,
    pcpMembers,
  ]);

  const sanitizedHtml = pageContent?.data?.page_text 
  ? DOMPurify.sanitize(pageContent.data.page_text, { RETURN_TRUSTED_TYPE: true })
  : '';

  return (
    <>
      <OrbitDrawer
        data-testid="providers-info-elect-drawer"
        drawerId="providers-info-elect-drawer"
        heading={t('dental.drawerHeading')}
        isOpen={openChooseProviderDrawer ?? false}
        handleClose={handleCloseChooseProviderDrawer}
        footer={true}
        saveButton={t('common:save')}
        handleSubmit={handleSubmitProviderInfoDetails}
        saveButtonDisabled={disableProviderDrawerInfoSaveButton ?? false}
        dialogLarge
        hTagImg={plan?.carrier_logo_url ? plan.carrier_logo_url : ''}
        hTagImgAlt={plan?.carrier}
      >
        <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }}></div>

        <h3 className="mt-5">{t('dental.providerCard.providerCardLabel')}</h3>
        <ProviderInfoCardStyled className="d-flex flex-column">
          <ProviderFamilyInfoCardStyled className="d-flex flex-column flex-md-row justify-content-between ">
            <ProviderFamilyBoxStyled
              variant="outlined"
              className="d-flex pl-2 pr-3 align-items-center mb-3 py-md-2 px-md-3"
            >
              <div>
                {displayDependentAvatar(subscriberData?.subscriber_profile, 40)}
              </div>
              <Typography variant="body1" className="ml-2">
                {t('dental.providerCard.subscriberName')}
              </Typography>
            </ProviderFamilyBoxStyled>

            {checkSubscriberCheckbox || pcpMembers?.pcp_name ? (
              <ProviderInfoBoxStyled>
                <div className="d-flex mb-3">
                  <div>
                    <Typography variant="body1" className="mr-1">
                      {t('dental.providerCard.providerNamelabel')}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="body1" fontWeight={600}>
                      {subscriberProviderDataName}
                    </Typography>
                  </div>
                </div>
                <div className="d-flex">
                  <div>
                    <Typography variant="body1" className="mr-1">
                      {t('dental.providerCard.providerCodelabel')}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="body1" fontWeight={600}>
                      {subscriberProviderDataNetworkCode}
                    </Typography>
                  </div>
                </div>

                <PsCheckboxTuner
                  inputId="existing-patient"
                  label={t('dental.providerCard.existingPatientBtnLabel')}
                  name="existing-provider"
                  checked={
                    pcpMembers?.existing_patient === undefined
                      ? subscriberProviderDataExistingPatient
                      : pcpMembers?.existing_patient
                  }
                  onClick={handleChangeSubscriberExistingPatient}
                />
                <PsButtonTuner
                  buttonId="change-btn"
                  data-testid="search-provider-dependent-info-btn"
                  level="tertiary"
                  show-underline
                  label={t('dental.providerCard.changeBtnLabel')}
                  onClick={handleChangeSubscriberProvider}
                  extraClass="p-0 mb-n2 mb-md-0"
                />
              </ProviderInfoBoxStyled>
            ) : (
              <PsButtonTuner
                buttonId="search-provider-subsriber-info-btn"
                data-testid="search-provider-subsriber-info-btn"
                level="tertiary"
                label={t('dental.providerCard.searchProviderBtnLabel')}
                icon="binoculars"
                onClick={handleSubscriberSearchProvider}
              />
            )}
          </ProviderFamilyInfoCardStyled>

          {dependentData?.map((dependent: any) => {
            const dependentId = `dependent-provider-${dependent.id}`;
            const isCheckboxChecked = checkDependentCheckbox[dependentId];

            const matchingItem =
              Array.isArray(saveCheckedDependentDataSelector) &&
              saveCheckedDependentDataSelector?.find(
                (item: any) => item?.id === dependent?.id
              );
            const dependentPcpData =
              Array.isArray(pcpMembers?.dependents) &&
              pcpMembers?.dependents.find(
                (depPcpData) => depPcpData.id === dependent.id
              );

            const providerName = matchingItem?.name
              ? matchingItem?.name
              : dependentPcpData && dependentPcpData?.pcp_name;
            const providerNetworkCode = matchingItem?.network_code
              ? matchingItem?.network_code
              : dependentPcpData && dependentPcpData?.pcp_code;

            const checkedExistingPatient =
              saveCheckedDependentDataSelector?.find(
                (item: any) => item.id === dependent.id
              )?.existingPatient;

            const existingPatient =
              checkedExistingPatient || isSelectProviderDrawerOpen
                ? checkedExistingPatient
                : dependentPcpData && dependentPcpData?.existing_patient;

            return (
              <ProviderFamilyInfoCardStyled
                key={dependentId}
                className="d-flex flex-column flex-md-row justify-content-between align-items-md-center"
              >
                <ProviderFamilyBoxStyled
                  variant="outlined"
                  className="d-flex pl-2 pr-3 align-items-center mb-3 py-2 px-3"
                >
                  <div>
                    {displayDependentAvatar(dependent?.dependent_profile, 40)}
                  </div>
                  <Typography
                    variant="body1"
                    className="ml-2"
                  >{`${dependent.first_name} ${getDependentAge(dependent.birthdate) !== 0 ? `(${getDependentAge(dependent.birthdate)})` : ''}`}</Typography>
                </ProviderFamilyBoxStyled>
                {isCheckboxChecked ||
                  (pcpMembers?.dependents &&
                    pcpMembers?.dependents?.length > 0 &&
                    (!_.isEmpty(providerName) ||
                      !_.isEmpty(providerNetworkCode))) ? (
                  <ProviderInfoBoxStyled>
                    <div className="d-flex mb-3">
                      <Typography variant="body1" className="mr-1">
                        {t('dental.providerCard.providerNamelabel')}
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {providerName}
                      </Typography>
                    </div>
                    <div className="d-flex">
                      <Typography variant="body1" className="mr-1">
                        {t('dental.providerCard.providerCodelabel')}
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {providerNetworkCode}
                      </Typography>
                    </div>
                    <PsCheckboxTuner
                      inputId={`existing-patient-btn-${dependentId}`}
                      label={t('dental.providerCard.existingPatientBtnLabel')}
                      name="existing-provider"
                      checked={Boolean(existingPatient)}
                      onClick={(e) =>
                        handleChangeDependentExistingPatient(
                          e,
                          dependentData,
                          providerItem
                        )
                      }
                    />
                    <PsButtonTuner
                      buttonId={`pcp-change-btn-${dependentId}`}
                      data-testid={`pcp-change-btn-${dependentId}`}
                      level="tertiary"
                      show-underline
                      extraClass="p-0 mb-n2 mb-md-0"
                      label={t('dental.providerCard.changeBtnLabel')}
                      onClick={(e) =>
                        handleChangeDependentProvider(e, dependentId)
                      }
                    />
                  </ProviderInfoBoxStyled>
                ) : (
                  <PsButtonTuner
                    buttonId={`search-provider-dependent-info-btn-${dependentId}`}
                    data-testid="search-provider-dependent-info-btn"
                    level="tertiary"
                    label={t('dental.providerCard.searchProviderBtnLabel')}
                    icon="binoculars"
                    onClick={() => handleDependentSearchProvider()}
                  />
                )}
              </ProviderFamilyInfoCardStyled>
            );
          })}
        </ProviderInfoCardStyled>
      </OrbitDrawer>
    </>
  );
};
